import React, { useState, useEffect } from 'react';
import styled, { createGlobalStyle, keyframes, css } from 'styled-components';
import { ethers } from 'ethers';
import axios from 'axios';
import { useAuth } from '../components/Auth/AuthContext';
import CardBalance from '../components/MyCard/CardBalance';
import { CreditCard, Zap, ChevronRight, Wallet, Calendar, DollarSign, Hash, RefreshCw, CheckCircle, Clock, TrendingUp, Activity, Sparkles, ArrowUpRight, Plus, X, Eye, EyeOff, Lock, Shield, Bell, Settings } from 'lucide-react';
import BANKCARD from '../assets/BankCard.png';


const isMobile = () => /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
const isInWalletBrowser = () => typeof window.ethereum !== 'undefined';



const GlobalStyle = createGlobalStyle`
  @font-face {
    font-family: 'Very Sans Light';
    src: url('/fonts/VerySans-LightText.ttf') format('truetype');
    font-weight: 300;
    font-style: normal;
    font-display: swap;
  }

  @font-face {
    font-family: 'Very Sans Medium';
    src: url('/fonts/VerySans-MediumText.ttf') format('truetype');
    font-weight: 500;
    font-style: normal;
    font-display: swap;
  }

  @font-face {
    font-family: 'Very Sans Bold';
    src: url('/fonts/VerySans-BoldText.ttf') format('truetype');
    font-weight: 700;
    font-style: normal;
    font-display: swap;
  }

  @font-face {
    font-family: 'Very Sans Headline';
    src: url('/fonts/VerySans-Headline.ttf') format('truetype');
    font-weight: 400;
    font-style: normal;
    font-display: swap;
  }

  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  body {
    font-family: 'Very Sans Medium', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    line-height: 1.6;
    color: #ffffff;
    background: linear-gradient(135deg, #020611 0%, #050B15 50%, #020611 100%);
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    overflow-x: hidden;
  }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to { opacity: 1; transform: translateY(0); }
`;

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to { opacity: 1; transform: translateY(0); }
`;

const shimmer = keyframes`
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
`;

const Container = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #020611 0%, #050B15 50%, #020611 100%);
  position: relative;
  overflow: hidden;
  max-width: 100%;
`;

const GradientOrb = styled.div`
  position: fixed;
  width: 800px;
  height: 800px;
  border-radius: 50%;
  filter: blur(120px);
  opacity: 0.25;
  pointer-events: none;
  animation: ${float} 10s ease-in-out infinite;
  
  &.orb-1 {
    top: -20%;
    left: -10%;
    background: radial-gradient(circle, rgba(59, 130, 246, 0.5) 0%, transparent 70%);
    animation-delay: 0s;
  }
  
  &.orb-2 {
    bottom: -20%;
    right: -10%;
    background: radial-gradient(circle, rgba(139, 92, 246, 0.5) 0%, transparent 70%);
    animation-delay: 3s;
  }
  
  @media (max-width: 768px) {
    width: 500px;
    height: 500px;
  }
`;

// PREMIUM NAVIGATION
const Nav = styled.nav`
  background: rgba(255, 255, 255, 0.02);
  backdrop-filter: blur(20px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  padding: 0 2rem;
  position: sticky;
  top: 0;
  z-index: 100;
`;

const NavInner = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 72px;
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const LogoIcon = styled.div`
  width: 32px;
  height: 32px;
  background: linear-gradient(135deg, #3B82F6 0%, #2563EB 100%);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const LogoText = styled.span`
  font-family: 'Very Sans Bold', sans-serif;
  font-size: 1.25rem;
  color: #ffffff;
  font-weight: 600;
  letter-spacing: -0.02em;
`;

const NavActions = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const IconButton = styled.button`
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  color: #94A3B8;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background: rgba(255, 255, 255, 0.08);
    border-color: rgba(59, 130, 246, 0.3);
    color: #ffffff;
  }

  @media screen and (max-width: 756px) {
  display: none;
  }
`;

const ConnectButton = styled.button`
  padding: 0 1.5rem;
  height: 40px;
  background: linear-gradient(135deg, #3B82F6 0%, #2563EB 100%);
  border: none;
  border-radius: 8px;
  color: #ffffff;
  font-family: 'Very Sans Bold', sans-serif;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  box-shadow: 0 4px 20px rgba(59, 130, 246, 0.3);
  
  &:hover {
    background: linear-gradient(135deg, #2563EB 0%, #1d4ed8 100%);
    transform: translateY(-2px);
    box-shadow: 0 6px 25px rgba(59, 130, 246, 0.4);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  @media screen and (max-width: 768px) {
  padding: 0 0.75rem;
  height: 30px;
  font-size: 0.6rem;
  font-weight: 500;
  gap: 0.25rem;
  }
`;

// MAIN LAYOUT
const Main = styled.main`
  max-width: 1400px;
  margin: 0 auto;
  padding: 3rem 2rem;
  position: relative;
  z-index: 2;
  
  @media (max-width: 768px) {
    padding: 2rem 1rem;
    max-width: 100%;
    margin: 0 auto;
  }
`;

const Header = styled.header`
  margin-bottom: 3rem;
  animation: ${fadeIn} 0.6s ease-out;
`;

const Title = styled.h1`
  font-family: 'Very Sans Headline', sans-serif;
  font-size: 3rem;
  color: #ffffff;
  font-weight: 600;
  letter-spacing: -0.03em;
  margin-bottom: 0.75rem;
  line-height: 1.1;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const Subtitle = styled.p`
  font-size: 1.125rem;
  color: #94A3B8;
  font-family: 'Very Sans Light', sans-serif;
  max-width: 600px;
`;

// TABS SYSTEM
const TabsContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  margin-bottom: 3rem;
`;

const Tab = styled.button`
  padding: 1rem 1.5rem;
  background: transparent;
  border: none;
  border-bottom: 2px solid ${props => props.active ? '#3B82F6' : 'transparent'};
  color: ${props => props.active ? '#ffffff' : '#94A3B8'};
  font-family: 'Very Sans Bold', sans-serif;
  font-size: 0.9375rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  
  &:hover {
    color: #ffffff;
    background: rgba(255, 255, 255, 0.03);
  }
`;

// CARD SECTION - PREMIUM DESIGN
const CardSection = styled.div`
  animation: ${slideUp} 0.8s ease-out;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  margin-bottom: 3rem;
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  background: rgba(255, 255, 255, 0.025);
  border: 1px solid rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  padding: 2rem;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.04);
    border-color: rgba(59, 130, 246, 0.3);
    box-shadow: 0 12px 30px rgba(59, 130, 246, 0.15);
  }

   @media (max-width: 756px) {
    margin: 0 auto;
    max-width: 100%;
    padding: 1.5rem;
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 2rem;
`;

const CardTitle = styled.h3`
  font-family: 'Very Sans Bold', sans-serif;
  font-size: 1.125rem;
  color: #ffffff;
  font-weight: 600;
  letter-spacing: -0.01em;
`;

const StatusBadge = styled.span`
  padding: 0.375rem 0.875rem;
  background: ${props => props.active ? 'rgba(34, 197, 94, 0.15)' : 'rgba(239, 68, 68, 0.15)'};
  color: ${props => props.active ? '#22c55e' : '#ef4444'};
  border: 1px solid ${props => props.active ? 'rgba(34, 197, 94, 0.3)' : 'rgba(239, 68, 68, 0.3)'};
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-family: 'Very Sans Bold', sans-serif;
`;

// VIRTUAL CARD DISPLAY - PREMIUM
const CardDisplay = styled.div`
  width: 100%;
  max-width: 420px;
  aspect-ratio: 1.586;
  background: url(${BANKCARD});
  background-size: cover;
  border-radius: 16px;
  padding: 2rem;
  position: relative;
  box-shadow: 
    0 20px 60px rgba(0, 0, 0, 0.5),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  margin: 0 auto 2rem;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 
      0 30px 80px rgba(0, 0, 0, 0.6),
      0 0 60px rgba(59, 130, 246, 0.15),
      inset 0 1px 0 rgba(255, 255, 255, 0.15);
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #3B82F6, #8B5CF6);
    border-radius: 16px 16px 0 0;
  }

  @media screen and (max-width: 756px) {
  width: 80vw;
  max-width: 756px;
  }

 
`;

const CardNumberDisplay = styled.div`
  font-family: 'SF Mono', 'Monaco', 'Courier New', monospace;
  font-size: 1rem;
  color: #FFFFFF;
  letter-spacing: 0.15em;
  text-align: center;
  line-height: 1.6;
  font-weight: 500;
  margin-bottom: 2rem;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.6);
  padding-left: 0.25rem;
  
  @media (max-width: 768px) {
    font-size: 1.2rem;
    letter-spacing: 0.2em;
  }
`;

const CardFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 2rem;
`;

const CardInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const CardLabel = styled.div`
  font-size: 0.5rem;
  color: rgba(255, 255, 255, 0.7);
  text-transform: uppercase;
  letter-spacing: 0.12em;
  font-weight: 500;
  font-family: 'Very Sans Medium', sans-serif;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.5);
`;

const CardValue = styled.div`
  font-size: 1rem;
  color: #FFFFFF;
  font-weight: 600;
  font-family: 'Very Sans Bold', sans-serif;
  text-shadow: 0 2px 6px rgba(0, 0, 0, 0.6);
  letter-spacing: 0.02em;
  position: absolute;
  left: 10px;
  bottom: 10px;
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

// ACTIONS
const ActionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
`;

const ActionButton = styled.button`
  padding: 1rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  color: #ffffff;
  font-family: 'Very Sans Bold', sans-serif;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  
  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.08);
    border-color: rgba(59, 130, 246, 0.3);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.2);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PrimaryButton = styled(ActionButton)`
  background: linear-gradient(135deg, #3B82F6 0%, #2563EB 100%);
  color: #ffffff;
  border: none;
  box-shadow: 0 4px 20px rgba(59, 130, 246, 0.3);
  
  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #2563EB 0%, #1d4ed8 100%);
    box-shadow: 0 6px 25px rgba(59, 130, 246, 0.4);
  }
`;

// INFO DISPLAY
const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
`;

const InfoItem = styled.div`
  padding: 1.25rem;
  background: rgba(255, 255, 255, 0.025);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.05);
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.04);
    border-color: rgba(59, 130, 246, 0.2);
  }
`;

const InfoLabel = styled.div`
  font-size: 0.8125rem;
  color: #94A3B8;
  margin-bottom: 0.5rem;
  font-weight: 500;
`;

const InfoValue = styled.div`
  font-family: 'Very Sans Bold', sans-serif;
  font-size: 1.125rem;
  color: #ffffff;
  font-weight: 600;
`;

// SUBSCRIPTIONS SECTION
const SubscriptionsSection = styled.div`
  animation: ${slideUp} 0.8s ease-out;
`;

const SubscriptionsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
`;

const AddButton = styled.button`
  padding: 0 1.5rem;
  height: 44px;
  background: linear-gradient(135deg, #3B82F6 0%, #2563EB 100%);
  border: none;
  border-radius: 8px;
  color: #ffffff;
  font-family: 'Very Sans Bold', sans-serif;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  box-shadow: 0 4px 20px rgba(59, 130, 246, 0.3);
  
  &:hover {
    background: linear-gradient(135deg, #2563EB 0%, #1d4ed8 100%);
    transform: translateY(-2px);
    box-shadow: 0 6px 25px rgba(59, 130, 246, 0.4);
  }
`;

const SubscriptionsList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
  gap: 1.5rem;
  
   @media (max-width: 768px) {
    grid-template-columns: 1fr;
    max-width: 100%;
    margin: 0 auto;
  }
`;

const SubscriptionCard = styled.div`
  background: rgba(255, 255, 255, 0.025);
  border: 1px solid rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  padding: 1.75rem;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: ${props => props.color || '#3B82F6'};
    box-shadow: 0 0 20px ${props => props.color || '#3B82F6'};
  }
  
  &:hover {
    background: rgba(255, 255, 255, 0.04);
    border-color: rgba(59, 130, 246, 0.3);
    box-shadow: 0 12px 30px rgba(59, 130, 246, 0.15);
    transform: translateY(-2px);
  }
`;

const SubHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1.5rem;
  gap: 1rem;
`;

const SubName = styled.h4`
  font-family: 'Very Sans Bold', sans-serif;
  font-size: 1.25rem;
  color: #ffffff;
  font-weight: 600;
  letter-spacing: -0.01em;
  margin: 0;
`;

const SubAmount = styled.div`
  font-family: 'Very Sans Bold', sans-serif;
  font-size: 1.5rem;
  color: #3B82F6;
  font-weight: 700;
  white-space: nowrap;
  
  &::before {
    content: '$';
    font-size: 1rem;
    color: #94A3B8;
    margin-right: 0.25rem;
  }
`;

const SubDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.875rem;
  padding: 1.25rem;
  background: rgba(255, 255, 255, 0.025);
  border-radius: 12px;
  margin-bottom: 1.5rem;
  border: 1px solid rgba(255, 255, 255, 0.05);
`;

const SubRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const SubLabel = styled.span`
  font-size: 0.875rem;
  color: #94A3B8;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const SubValue = styled.span`
  font-family: 'Very Sans Bold', sans-serif;
  font-size: 0.875rem;
  color: #ffffff;
  font-weight: 600;
`;

const SubActions = styled.div`
  display: flex;
  gap: 0.75rem;
  padding-top: 1.25rem;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
`;

const SubButton = styled.button`
  flex: 1;
  padding: 0.75rem;
  background: ${props => props.danger ? 'rgba(239, 68, 68, 0.15)' : 'rgba(255, 255, 255, 0.05)'};
  border: 1px solid ${props => props.danger ? 'rgba(239, 68, 68, 0.3)' : 'rgba(255, 255, 255, 0.1)'};
  border-radius: 8px;
  color: ${props => props.danger ? '#ef4444' : '#ffffff'};
  font-family: 'Very Sans Bold', sans-serif;
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${props => props.danger ? 'rgba(239, 68, 68, 0.2)' : 'rgba(255, 255, 255, 0.08)'};
    transform: translateY(-1px);
  }
`;

// MODAL
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(8px);
  display: ${props => props.show ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 2rem;
  animation: ${fadeIn} 0.2s ease-out;
`;

const Modal = styled.div`
  background: #050B15;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 2rem;
  max-width: 560px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(90deg, #3B82F6, #8B5CF6);
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const ModalTitle = styled.h3`
  font-family: 'Very Sans Bold', sans-serif;
  font-size: 1.5rem;
  color: #ffffff;
  font-weight: 600;
  letter-spacing: -0.02em;
`;

const CloseButton = styled.button`
  width: 36px;
  height: 36px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  color: #94A3B8;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background: rgba(255, 255, 255, 0.08);
    color: #ffffff;
  }
`;

const FormGrid = styled.div`
  display: grid;
  gap: 1.5rem;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-family: 'Very Sans Bold', sans-serif;
  font-size: 0.875rem;
  color: #ffffff;
  font-weight: 600;
`;

const Input = styled.input`
  padding: 0.875rem 1rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  color: #ffffff;
  font-size: 0.9375rem;
  font-family: 'Very Sans Medium', sans-serif;
  transition: all 0.2s ease;
  
  &::placeholder {
    color: #64748B;
  }
  
  &:focus {
    outline: none;
    border-color: #3B82F6;
    background: rgba(255, 255, 255, 0.08);
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
  
  &:read-only {
    background: rgba(255, 255, 255, 0.02);
    color: #94A3B8;
    cursor: not-allowed;
  }
`;

const InputRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const ButtonRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-top: 1.5rem;
`;

const SecondaryButton = styled.button`
  padding: 1rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  color: #ffffff;
  font-family: 'Very Sans Bold', sans-serif;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  
  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.08);
    border-color: rgba(59, 130, 246, 0.3);
    transform: translateY(-1px);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PrimaryButtonLarge = styled.button`
  padding: 1rem;
  background: linear-gradient(135deg, #3B82F6 0%, #2563EB 100%);
  border: none;
  border-radius: 8px;
  color: #ffffff;
  font-family: 'Very Sans Bold', sans-serif;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  box-shadow: 0 4px 20px rgba(59, 130, 246, 0.3);
  
  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #2563EB 0%, #1d4ed8 100%);
    transform: translateY(-2px);
    box-shadow: 0 6px 25px rgba(59, 130, 246, 0.4);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Alert = styled.div`
  padding: 1rem;
  background: ${props => props.type === 'error' ? 'rgba(239, 68, 68, 0.15)' : 'rgba(245, 158, 11, 0.15)'};
  border: 1px solid ${props => props.type === 'error' ? 'rgba(239, 68, 68, 0.3)' : 'rgba(245, 158, 11, 0.3)'};
  border-radius: 8px;
  color: ${props => props.type === 'error' ? '#ef4444' : '#f59e0b'};
  font-size: 0.875rem;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
`;

const AlertButton = styled.button`
  padding: 0.5rem 1rem;
  background: ${props => props.type === 'error' ? 'rgba(239, 68, 68, 0.2)' : 'rgba(245, 158, 11, 0.2)'};
  border: 1px solid ${props => props.type === 'error' ? 'rgba(239, 68, 68, 0.4)' : 'rgba(245, 158, 11, 0.4)'};
  border-radius: 6px;
  color: ${props => props.type === 'error' ? '#ef4444' : '#f59e0b'};
  font-family: 'Very Sans Bold', sans-serif;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  
  &:hover {
    background: ${props => props.type === 'error' ? 'rgba(239, 68, 68, 0.3)' : 'rgba(245, 158, 11, 0.3)'};
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  gap: 1.5rem;
`;

const Spinner = styled.div`
  width: 48px;
  height: 48px;
  border: 3px solid rgba(59, 130, 246, 0.2);
  border-top-color: #3B82F6;
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`;

const LoadingText = styled.p`
  font-size: 0.9375rem;
  color: #94A3B8;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
`;

const EmptyIcon = styled.div`
  width: 64px;
  height: 64px;
  margin: 0 auto 1.5rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const EmptyTitle = styled.h3`
  font-family: 'Very Sans Bold', sans-serif;
  font-size: 1.125rem;
  color: #ffffff;
  margin-bottom: 0.5rem;
`;

const EmptyText = styled.p`
  font-size: 0.9375rem;
  color: #94A3B8;
`;

// Keep all your existing ABIs
const ERC20_ABI = [
  "function approve(address spender, uint256 amount) public returns (bool)",
  "function allowance(address owner, address spender) external view returns (uint256)",
  "function decimals() view returns (uint8)"
];

const AUTO_PULL_PAYMENT_ABI = [
  "function subscribe(string memory subscriptionName, uint256 amount, uint256 nextPaymentDate, uint256 numPayments) external",
  "event SubscriptionCreated(address indexed user, string subscriptionName, uint256 amount, uint256 nextPaymentDate)"
];

const GET_USER_SUBSCRIPTIONS_ABI = [
  {
    "inputs": [
      { "internalType": "address", "name": "user", "type": "address" }
    ],
    "name": "getUserActiveSubscriptions",
    "outputs": [
      { "internalType": "string[]", "name": "subscriptionNames", "type": "string[]" },
      { 
        "internalType": "struct AutoPullPayment.Subscription[]", 
        "name": "subscriptionData", 
        "type": "tuple[]",
        "components": [
          { "internalType": "uint256", "name": "amount", "type": "uint256" },
          { "internalType": "uint256", "name": "nextPaymentDate", "type": "uint256" },
          { "internalType": "uint256", "name": "remaining", "type": "uint256" },
          { "internalType": "uint256", "name": "totalPayments", "type": "uint256" },
          { "internalType": "uint256", "name": "createdAt", "type": "uint256" },
          { "internalType": "bool", "name": "isActive", "type": "bool" }
        ]
      }
    ],
    "stateMutability": "view",
    "type": "function"
  }
];

const AutoPullPage = () => {
    const [activeTab, setActiveTab] = useState('card');
    const [cardDetails, setCardDetails] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [activating, setActivating] = useState(false);
    const [cardId, setCardId] = useState(null);
    const { user } = useAuth();
    const [showModal, setShowModal] = useState(false);

    // Subscription state
    const [tokenAddress, setTokenAddress] = useState("");
    const [amount, setAmount] = useState("");
    const [subscriptionName, setSubscriptionName] = useState("");
    const [nextPaymentDate, setNextPaymentDate] = useState("");
    const [numPayments, setNumPayments] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [provider, setProvider] = useState(null);
    const [contract, setContract] = useState(null);
    const [walletAddress, setWalletAddress] = useState(null);
    const [approved, setApproved] = useState(false);
    const [totalAmount, setTotalAmount] = useState(0);
    const [currentNetwork, setCurrentNetwork] = useState(null);
    const [userSubscriptions, setUserSubscriptions] = useState([]);
    const [wcProvider, setWcProvider] = useState(null);

    




    useEffect(() => {
        return () => {
            if (wcProvider) {
                wcProvider.disconnect();
            }
        };
    }, [wcProvider]);

    const networks = {
        hydrix: {
            chainId: '0x539',
            chainName: 'Hydrix Testnet',
            rpcUrls: [
                'https://chainfree.info:5010/validator/1',
                'https://chainfree.info:5010/validator/2',
                'https://chainfree.info:5010/validator/3'
            ],
            nativeCurrency: { name: 'HYX', symbol: 'HYX', decimals: 18 }
        }
    };

    const createProviderWithFallback = async (chainId) => {
        let network = null;
        if (chainId === '0x539') network = networks.hydrix;
        if (network) {
            for (const rpcUrl of network.rpcUrls) {
                try {
                    const provider = new ethers.providers.JsonRpcProvider(rpcUrl);
                    await provider.getBlockNumber();
                    return provider;
                } catch (error) {
                    continue;
                }
            }
        }
        return new ethers.providers.Web3Provider(window.ethereum);
    };

    const getCurrentNetwork = async () => {
        if (typeof window.ethereum !== 'undefined') {
            try {
                const chainId = await window.ethereum.request({ method: 'eth_chainId' });
                setCurrentNetwork(chainId);
                return chainId;
            } catch (error) {
                console.error('Error getting network:', error);
            }
        }
        return null;
    };

   const switchToHydrix = async (instance = window.ethereum) => {
    if (instance) {
        try {
            await instance.request({
                method: 'wallet_switchEthereumChain',
                params: [{ chainId: networks.hydrix.chainId }],
            });
        } catch (switchError) {
            if (switchError.code === 4902) {
                try {
                    await instance.request({
                        method: 'wallet_addEthereumChain',
                        params: [networks.hydrix],
                    });
                } catch (addError) {
                    setError('Failed to add Hydrix network');
                }
            }
        }
    }
};

const connectWallet = async () => {
    // Mobile: must use wallet browser
    if (isMobile() && !isInWalletBrowser()) {
        setError('On mobile, please open this page in your MetaMask mobile browser. Open MetaMask app → Browser → paste this URL');
        return;
    }
    
    if (typeof window.ethereum !== 'undefined') {
        try {
            await window.ethereum.request({ method: 'eth_requestAccounts' });
            
            const currentChainId = await window.ethereum.request({ method: 'eth_chainId' });
            
            if (currentChainId !== '0x539') {
                await switchToHydrix();
                await new Promise(resolve => setTimeout(resolve, 1500));
            }
            
            const metamaskProvider = new ethers.providers.Web3Provider(window.ethereum);
            setProvider(metamaskProvider);
            
            const signer = metamaskProvider.getSigner();
            const address = await signer.getAddress();
            setWalletAddress(address);
            
            try {
                await axios.post('https://chainfree.info:7001/user/link-wallet', {
                    wallet_address: address,
                    wallet_name: 'My Wallet'
                }, {
                    headers: { 
                        'Authorization': `Bearer ${localStorage.getItem('authToken')}` 
                    }
                });
            } catch (linkError) {
                console.error('Failed to link wallet:', linkError);
            }
            
            const finalChainId = await window.ethereum.request({ method: 'eth_chainId' });
            setCurrentNetwork(finalChainId);
            
            const contractAddress = "0xC0B1181858c85ed216925f55062E72Cd5a67c640";
            const contract = new ethers.Contract(contractAddress, AUTO_PULL_PAYMENT_ABI, signer);
            setContract(contract);
            
            window.ethereum.on('chainChanged', () => window.location.reload());
            window.ethereum.on('accountsChanged', () => window.location.reload());
            
        } catch (error) {
            console.error("Connect wallet error:", error);
            setError('Failed to connect wallet. Please try again.');
        }
    } else {
        setError('Please install MetaMask browser extension');
    }
};

    const knownTokens = {
        '0xc4d635d00f4d3e731b1c99ae0dfe662fdc994334': { decimals: 6, symbol: 'USDH', name: 'USD Hydrix', network: 'hydrix' }
    };

    const getTokenDecimals = async (tokenAddr) => {
        const normalizedAddress = tokenAddr.toLowerCase();
        if (knownTokens[normalizedAddress]) return knownTokens[normalizedAddress].decimals;
        return 6;
    };

    const approveToken = async () => {
        const network = await provider.getNetwork();
        if (!provider || !amount || !nextPaymentDate || !numPayments) {
            setError("Please provide all necessary details.");
            return;
        }
        try {
            setError("");
            
            const signer = provider.getSigner();
            const tokenAddress = "0xc4d635D00F4d3e731B1C99ae0DFe662fdc994334";
            
            const tokenContract = new ethers.Contract(tokenAddress, ERC20_ABI, signer);
            const spender = "0xC0B1181858c85ed216925f55062E72Cd5a67c640";
            
            const decimals = await getTokenDecimals(tokenAddress);
            const amountInUnits = ethers.utils.parseUnits(amount, decimals);
            const numPaymentsInt = parseInt(numPayments, 10);
            const totalAmountInUnits = amountInUnits.mul(numPaymentsInt);
            
            const allowance = await tokenContract.allowance(walletAddress, spender);
            
            if (allowance.lt(totalAmountInUnits)) {
                const tx = await tokenContract.approve(spender, totalAmountInUnits);
                await tx.wait();
                setApproved(true);
                alert("Token approved successfully!");
            } else {
                setApproved(true);
                alert("You have already approved this amount.");
            }
        } catch (err) {
            console.error("Approval error:", err);
            
            if (err.code === 4001) {
                setError("Transaction rejected by user.");
            } else if (err.code === -32603) {
                setError("Internal error. Check if you have USDH tokens and are on Hydrix network.");
            } else if (err.message?.includes("insufficient funds")) {
                setError("Insufficient HYX for gas fees.");
            } else {
                setError(`Approval failed: ${err.message || "Please check your wallet and network."}`);
            }
        }
    };

    const subscribeToPayment = async () => {
        if (!provider || !contract || !amount || !subscriptionName || !nextPaymentDate || !numPayments) {
            setError("Please fill in all fields and connect your wallet.");
            return;
        }

        if (!approved) {
            setError("Please approve tokens first.");
            return;
        }

        try {
            setIsSubmitting(true);
            setError("");

            const signer = provider.getSigner();
            const tokenAddress = "0xc4d635D00F4d3e731B1C99ae0DFe662fdc994334";
            
            const decimals = await getTokenDecimals(tokenAddress);
            const amountInUnits = ethers.utils.parseUnits(amount, decimals);
            const nextPaymentTimestamp = Math.floor(new Date(nextPaymentDate).getTime() / 1000);

            const tx = await contract.subscribe(
                subscriptionName,
                amountInUnits,
                nextPaymentTimestamp,
                parseInt(numPayments, 10)
            );

            await tx.wait();

            try {
                await axios.post('https://chainfree.info:7001/user/link-subscription', {
                    subscription_name: subscriptionName,
                    wallet_address: walletAddress,
                    amount: amount,
                    num_payments: parseInt(numPayments, 10),
                    next_payment_date: nextPaymentTimestamp
                }, {
                    headers: { 
                        'Authorization': `Bearer ${localStorage.getItem('authToken')}` 
                    }
                });
            } catch (linkError) {
                console.error('Failed to link subscription to database:', linkError);
                setError('Subscription created on blockchain but failed to link to your account. Please contact support.');
            }

            alert("Subscription created successfully!");
            
            setSubscriptionName("");
            setAmount("");
            setNextPaymentDate("");
            setNumPayments("");
            setApproved(false);
            setShowModal(false);
            
            await getUserActiveSubscriptions();

        } catch (err) {
            console.error("Error creating subscription:", err);
            setError(`Failed to create subscription: ${err.message}`);
        } finally {
            setIsSubmitting(false);
        }
    };

    const getUserActiveSubscriptions = async () => {
        if (!walletAddress || !provider) return;
        try {
            const contractAddress = "0xC0B1181858c85ed216925f55062E72Cd5a67c640";
            const contract = new ethers.Contract(contractAddress, GET_USER_SUBSCRIPTIONS_ABI, provider);
            const result = await contract.getUserActiveSubscriptions(walletAddress);
            const [subscriptionNames, subscriptionData] = result;
            const formattedSubscriptions = subscriptionNames.map((name, index) => ({
                name: name,
                amount: ethers.utils.formatUnits(subscriptionData[index].amount, 6),
                nextPaymentDate: new Date(subscriptionData[index].nextPaymentDate.toNumber() * 1000).toLocaleDateString(),
                remaining: subscriptionData[index].remaining.toNumber(),
                totalPayments: subscriptionData[index].totalPayments.toNumber(),
                createdAt: new Date(subscriptionData[index].createdAt.toNumber() * 1000).toLocaleDateString(),
                isActive: subscriptionData[index].isActive
            }));
            setUserSubscriptions(formattedSubscriptions);
        } catch (error) {
            console.error('Error fetching subscriptions:', error);
        }
    };

    useEffect(() => {
        if (walletAddress) {
            const timer = setTimeout(() => getUserActiveSubscriptions(), 1000);
            return () => clearTimeout(timer);
        }
    }, [walletAddress]);

    const getCurrentDateTime = () => {
        const now = new Date();
        now.setHours(now.getHours() + 1);
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        const hours = String(now.getHours()).padStart(2, '0');
        const minutes = String(now.getMinutes()).padStart(2, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}`;
    };

    const activateCard = async () => {
        try {
            setActivating(true);
            setError('');
            const response = await axios.post('https://chainfree.info:7001/user/activate-card', {}, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` }
            });
            if (response.data.success) {
                await fetchUserCardDetails();
                alert('Card activated successfully!');
            } else {
                setError(response.data.message || 'Failed to activate card');
            }
        } catch (error) {
            setError('Failed to activate card. Please try again.');
        } finally {
            setActivating(false);
        }
    };

    const fetchUserCardDetails = async () => {
        try {
            setLoading(true);
            setError('');
            const userCardsResponse = await axios.get('https://chainfree.info:7001/user/cards', {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` }
            });
            if (!userCardsResponse.data.success || userCardsResponse.data.data.length === 0) {
                setError('No virtual card found. Please create a card first.');
                setLoading(false);
                return;
            }
            const userCard = userCardsResponse.data.data[0];
            const cardId = userCard.card_id;
            setCardId(cardId);
            const cardDetailsResponse = await axios.post('https://chainfree.info:7000/retrieve-card-details', { cardId });
            const data = cardDetailsResponse.data;
            if (data.cardDetails || data.card || data) {
                const cardInfo = data.cardDetails || data.card || data;
                setCardDetails(cardInfo);
                localStorage.setItem('cardId', cardId);
            }
        } catch (error) {
            setError('Failed to fetch card details.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (user) {
            fetchUserCardDetails();
        } else {
            setError('Please log in to view your card details.');
            setLoading(false);
        }
    }, [user]);

    const handleRefresh = () => {
        if (user) fetchUserCardDetails();
    };

    const formatDate = (timestamp) => {
        return new Date(timestamp * 1000).toLocaleDateString();
    };

    const subscriptionColors = ['#3B82F6', '#8b5cf6', '#ec4899', '#f59e0b', '#22c55e'];
    
    if (!user) {
        return (
            <>
                <GlobalStyle />
                <Container>
                    <Alert type="error">Please log in to access your dashboard.</Alert>
                </Container>
            </>
        );
    }

    return (
        <>
            <GlobalStyle />
            <Container>
                <GradientOrb className="orb-1" />
                <GradientOrb className="orb-2" />

                <Nav>
                    <NavInner>
                        <Logo>
                            <LogoIcon>
                                <CreditCard size={18} color="#FFFFFF" />
                            </LogoIcon>
                            <LogoText>HydrixPay</LogoText>
                        </Logo>
                        
                        <NavActions>
                            <IconButton>
                                <Bell size={20} />
                            </IconButton>
                            <IconButton>
                                <Settings size={20} />
                            </IconButton>
                            <ConnectButton onClick={connectWallet}>
                                <Wallet size={18} />
                                {!walletAddress ? "Connect wallet" : `${walletAddress.slice(0, 6)}...${walletAddress.slice(-4)}`}
                            </ConnectButton>
                        </NavActions>
                    </NavInner>
                </Nav>

                <Main>
                    <Header>
                        <Title>Payment Dashboard</Title>
                        <Subtitle>Manage your virtual card and recurring subscriptions</Subtitle>
                    </Header>

                    <TabsContainer>
                        <Tab active={activeTab === 'card'} onClick={() => setActiveTab('card')}>
                            Virtual Card
                        </Tab>
                        <Tab active={activeTab === 'subscriptions'} onClick={() => setActiveTab('subscriptions')}>
                            Subscriptions
                        </Tab>
                    </TabsContainer>

                    {activeTab === 'card' && (
                        <>
                            {loading && (
                                <LoadingContainer>
                                    <Spinner />
                                    <LoadingText>Loading your card details...</LoadingText>
                                </LoadingContainer>
                            )}
                            
                            {error && !loading && (
                                <>
                                    <Alert type="error">
                                        {error}
                                    </Alert>
                                    <div style={{ textAlign: 'center' }}>
                                        <PrimaryButton onClick={handleRefresh}>
                                            <RefreshCw size={18} />
                                            Retry
                                        </PrimaryButton>
                                    </div>
                                </>
                            )}
                            
                            {cardDetails && !loading && (
                                <CardSection>
                                    <Grid>
                                        <Card>
                                            <CardHeader>
                                                <CardTitle>Your Card</CardTitle>
                                                <StatusBadge active={cardDetails.status === 'active'}>
                                                    {cardDetails.status}
                                                </StatusBadge>
                                            </CardHeader>
                                            
                                            <CardDisplay>
                                            
                                          </CardDisplay>
                                            <ActionsGrid>
                                                {cardDetails.status === 'inactive' && (
                                                    <PrimaryButton onClick={activateCard} disabled={activating}>
                                                        <CheckCircle size={18} />
                                                        {activating ? 'Activating...' : 'Activate Card'}
                                                    </PrimaryButton>
                                                )}
                                                <ActionButton onClick={handleRefresh}>
                                                    <RefreshCw size={18} />
                                                    Refresh
                                                </ActionButton>
                                            </ActionsGrid>
                                        </Card>

                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
                                            <CardBalance cardId={cardId} />
                                            
                                            <Card>
                                                <CardHeader>
                                                    <CardTitle>Card Details</CardTitle>
                                                </CardHeader>
                                                
                                                <InfoGrid>
                                                    <InfoItem>
                                                        <InfoLabel>Brand</InfoLabel>
                                                        <InfoValue>{cardDetails.brand}</InfoValue>
                                                    </InfoItem>
                                                    <InfoItem>
                                                        <InfoLabel>Type</InfoLabel>
                                                        <InfoValue>Virtual</InfoValue>
                                                    </InfoItem>
                                                    <InfoItem>
                                                        <InfoLabel>Created</InfoLabel>
                                                        <InfoValue>{formatDate(cardDetails.created)}</InfoValue>
                                                    </InfoItem>
                                                    <InfoItem>
                                                        <InfoLabel>Holder</InfoLabel>
                                                        <InfoValue>{user.full_name}</InfoValue>
                                                    </InfoItem>
                                                </InfoGrid>
                                            </Card>
                                        </div>
                                    </Grid>
                                </CardSection>
                            )}
                        </>
                    )}

                    {activeTab === 'subscriptions' && (
                        <>
                            <SubscriptionsSection>
                                <SubscriptionsHeader>
                                    <div>
                                        <CardTitle style={{ marginBottom: '0.5rem' }}>Active Subscriptions</CardTitle>
                                        <Subtitle style={{ marginBottom: 0 }}>Automated recurring payments</Subtitle>
                                    </div>
                                    <AddButton onClick={() => setShowModal(true)}>
                                        <Plus size={18} />
                                        New subscription
                                    </AddButton>
                                </SubscriptionsHeader>

                                {userSubscriptions.length > 0 ? (
                                    <SubscriptionsList>
                                        {userSubscriptions.map((sub, index) => (
                                            <SubscriptionCard key={index} color={subscriptionColors[index % subscriptionColors.length]}>
                                                <SubHeader>
                                                    <SubName>{sub.name}</SubName>
                                                    <SubAmount>{sub.amount}</SubAmount>
                                                </SubHeader>
                                                
                                                <SubDetails>
                                                    <SubRow>
                                                        <SubLabel>
                                                            <Calendar size={16} />
                                                            Next payment
                                                        </SubLabel>
                                                        <SubValue>{sub.nextPaymentDate}</SubValue>
                                                    </SubRow>
                                                    
                                                    <SubRow>
                                                        <SubLabel>
                                                            <Hash size={16} />
                                                            Remaining
                                                        </SubLabel>
                                                        <SubValue>{sub.remaining} / {sub.totalPayments}</SubValue>
                                                    </SubRow>
                                                    
                                                    <SubRow>
                                                        <SubLabel>
                                                            <Activity size={16} />
                                                            Status
                                                        </SubLabel>
                                                        <SubValue style={{ color: sub.isActive ? '#22c55e' : '#ef4444' }}>
                                                            {sub.isActive ? 'Active' : 'Paused'}
                                                        </SubValue>
                                                    </SubRow>
                                                </SubDetails>
                                                
                                                <SubActions>
                                                    <SubButton danger>Cancel</SubButton>
                                                </SubActions>
                                            </SubscriptionCard>
                                        ))}
                                    </SubscriptionsList>
                                ) : (
                                    <EmptyState>
                                        <EmptyIcon>
                                            <Zap size={28} color="#94A3B8" />
                                        </EmptyIcon>
                                        <EmptyTitle>No subscriptions yet</EmptyTitle>
                                        <EmptyText>Create your first automated payment</EmptyText>
                                    </EmptyState>
                                )}
                            </SubscriptionsSection>
                        </>
                    )}
                </Main>

                <ModalOverlay show={showModal} onClick={() => setShowModal(false)}>
                    <Modal onClick={(e) => e.stopPropagation()}>
                        <ModalHeader>
                            <ModalTitle>New Subscription</ModalTitle>
                            <CloseButton onClick={() => setShowModal(false)}>
                                <X size={20} />
                            </CloseButton>
                        </ModalHeader>

                        {error && (
                            <Alert type="error">
                                {error}
                            </Alert>
                        )}
                        
                        {currentNetwork && currentNetwork !== '0x539' && (
                            <Alert>
                                <span>Please switch to Hydrix network</span>
                                <AlertButton onClick={switchToHydrix}>
                                    Switch Network
                                </AlertButton>
                            </Alert>
                        )}

                        <FormGrid>
                            <InputGroup>
                                <Label>Subscription Name</Label>
                                <Input
                                    type="text"
                                    placeholder="Netflix, Spotify..."
                                    value={subscriptionName}
                                    onChange={(e) => setSubscriptionName(e.target.value)}
                                />
                            </InputGroup>
                            
                            <InputGroup>
                                <Label>Amount per Payment</Label>
                                <Input
                                    type="text"
                                    placeholder="10.00"
                                    value={amount}
                                    onChange={(e) => setAmount(e.target.value)}
                                />
                            </InputGroup>
                            
                            <InputRow>
                                <InputGroup>
                                    <Label>First Payment</Label>
                                    <Input
                                        type="datetime-local"
                                        value={nextPaymentDate}
                                        onChange={(e) => setNextPaymentDate(e.target.value)}
                                        min={getCurrentDateTime()}
                                    />
                                </InputGroup>
                                <InputGroup>
                                    <Label>Total Payments</Label>
                                    <Input
                                        type="number"
                                        placeholder="12"
                                        value={numPayments}
                                        onChange={(e) => setNumPayments(e.target.value)}
                                    />
                                </InputGroup>
                            </InputRow>
                            
                            <InputGroup>
                                <Label>Payment Token</Label>
                                <Input
                                    type="text"
                                    value="USDH (Hydrix Stablecoin)"
                                    readOnly
                                />
                            </InputGroup>
                        </FormGrid>

                        <ButtonRow>
                            <SecondaryButton onClick={approveToken} disabled={isSubmitting}>
                                <Shield size={18} />
                                {isSubmitting ? "Approving..." : "1. Approve"}
                            </SecondaryButton>
                            <PrimaryButtonLarge onClick={subscribeToPayment} disabled={isSubmitting || !approved}>
                                <Zap size={18} />
                                {isSubmitting ? "Creating..." : "2. Create"}
                            </PrimaryButtonLarge>
                        </ButtonRow>
                    </Modal>
                </ModalOverlay>
            </Container>
        </>
    );
};

export default AutoPullPage;